import { Input } from "@openup/shared/primitives/ui/input";
import { Textarea } from "@openup/shared/primitives/ui/textarea";
import { Card, CardContent } from "@openup/shared/primitives/ui/card";
import { Badge } from "@openup/shared/primitives/ui/badge";
import { HostableExpert, SpaceEvent } from "@src/models/spaces";
import FormField from "@src/components/spaces/FormField";
import SessionExperts from "@src/components/spaces/SessionExperts";
import { Select } from "@src/components/spaces/ui";
import { DURATION_OPTIONS } from "@src/components/spaces/createEventForm";
import { TIMEZONES } from "@src/components/spaces/timezones";
import {
  EditSessionFormState,
  isSessionRescheduled,
} from "@src/components/spaces/editEventForm";
import { formatSessionDateTime, formatSessionTime } from "@src/utils/format";

interface SessionEditCardProps {
  index: number;
  session: EditSessionFormState;
  event: SpaceEvent;
  experts: HostableExpert[];
  capacityError?: string;
  expertsError?: string;
  /** Structured rejection from the PATCH (e.g. capacity below registered count). */
  serverError?: string;
  onChange: (partial: Partial<EditSessionFormState>) => void;
}

/**
 * One session in the edit-event form (OI-24717). Name, capacity, instructions,
 * date/time/timezone, and experts are all editable; changing the time is a
 * reschedule (reveals the reason field and notifies attendees). Cancelled and
 * completed sessions are fully read-only.
 */
const SessionEditCard = ({
  index,
  session,
  event,
  experts,
  capacityError,
  expertsError,
  serverError,
  onChange,
}: SessionEditCardProps) => {
  const original = event.sessions.find((s) => s.id === session.id);

  if (!session.editable) {
    return (
      <Card>
        <CardContent className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-indigo-800">
              Session {index + 1}
            </h3>
            <Badge variant="light">
              {original?.status === "cancelled" ? "Cancelled" : "Completed"} —
              read-only
            </Badge>
          </div>
          {original && (
            <p className="text-sm text-gray-600">
              {formatSessionDateTime(original.startsAt)}–
              {formatSessionTime(original.endsAt)} ({original.timeZone}) ·{" "}
              {original.experts.map((e) => e.fullName).join(", ") ||
                "No experts"}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const rescheduled = original
    ? isSessionRescheduled(session, original)
    : false;
  // Keep the current length selectable even when it isn't one of the presets.
  const durationOptions = [
    ...new Set<number>([...DURATION_OPTIONS, session.durationMinutes]),
  ].sort((a, b) => a - b);

  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="mb-4 text-base font-semibold text-indigo-800">
          Session {index + 1}
        </h3>

        {serverError && (
          <p className="mb-3 text-sm text-red-600" role="alert">
            {serverError}
          </p>
        )}

        <div className="mb-4">
          <FormField label="Session name">
            {({ id }) => (
              <Input
                id={id}
                value={session.name}
                placeholder="Optional — defaults to the event title"
                onChange={(e) => onChange({ name: e.target.value })}
              />
            )}
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Date" required>
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={session.date}
                onChange={(e) => onChange({ date: e.target.value })}
              />
            )}
          </FormField>

          <FormField label="Start time" required>
            {({ id }) => (
              <Input
                id={id}
                type="time"
                value={session.startTime}
                onChange={(e) => onChange({ startTime: e.target.value })}
              />
            )}
          </FormField>

          <FormField label="Length" required>
            {() => (
              <div
                role="group"
                aria-label="Length"
                className="flex w-full divide-x divide-gray-300 overflow-hidden rounded-lg border border-gray-300"
              >
                {durationOptions.map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    aria-pressed={session.durationMinutes === minutes}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      session.durationMinutes === minutes
                        ? "bg-green-700 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => onChange({ durationMinutes: minutes })}
                  >
                    {minutes} min
                  </button>
                ))}
              </div>
            )}
          </FormField>

          <FormField label="Timezone">
            {({ id }) => (
              <Select
                id={id}
                value={session.timeZone}
                onChange={(e) => onChange({ timeZone: e.target.value })}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Select>
            )}
          </FormField>

          <FormField label="Capacity" required error={capacityError}>
            {({ id, invalid }) => (
              <Input
                id={id}
                type="number"
                min={1}
                value={session.capacity}
                aria-invalid={invalid}
                onChange={(e) => onChange({ capacity: e.target.value })}
              />
            )}
          </FormField>
        </div>

        <div className="mt-4">
          <FormField label="Session instructions">
            {({ id }) => (
              <Textarea
                id={id}
                rows={3}
                maxLength={2000}
                value={session.sessionInstructions}
                placeholder="Optional — practical notes for attendees, e.g. what to prepare"
                onChange={(e) =>
                  onChange({ sessionInstructions: e.target.value })
                }
              />
            )}
          </FormField>
        </div>

        <SessionExperts
          assigned={session.experts}
          experts={experts}
          eventType={event.eventType}
          error={expertsError}
          onChange={(next) => onChange({ experts: next })}
        />

        {rescheduled && (
          <div className="mt-4 rounded-lg bg-orange-100 p-4">
            <FormField label="Reason for rescheduling">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={2}
                  maxLength={2000}
                  value={session.rescheduleInfo}
                  placeholder="Optional — shown to attendees in the reschedule email"
                  onChange={(e) => onChange({ rescheduleInfo: e.target.value })}
                />
              )}
            </FormField>
            <p className="mt-1 text-xs text-indigo-800">
              You changed the time — registered attendees will be emailed about
              the new schedule when you save.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionEditCard;
